import { useCallback, useEffect, useRef } from 'react';

interface LockResponse {
  expiresAt?: string;
  lockExpiry?: string;
  expiry?: string;
  message?: string;
  [key: string]: unknown;
}

interface UseLockRefreshParams {
  id: number | null | undefined;
  isEditMode: boolean;
  lockGlobalAction: (id: number | null | undefined) => { unwrap: () => Promise<LockResponse> };
  unlockGlobalAction: (id: number | null | undefined) => { unwrap: () => Promise<unknown> };
}

interface UseLockRefreshReturn {
  clearLockRefreshTimer: () => void;
}

const extractLockExpiry = (response: LockResponse): string | null =>
  response?.expiresAt ?? response?.lockExpiry ?? response?.expiry ?? null;

/**
 * Manages the lock refresh lifecycle for an edit-mode global action.
 *
 * On mount (when isEditMode is true) it immediately refreshes the lock to
 * obtain its expiry, then schedules repeated refreshes at 75 % of the
 * remaining TTL (minimum 5 s, 4-minute fallback when no expiry is returned).
 * The timer is cleared automatically on unmount.
 *
 * @param params.id              - Global action ID being edited
 * @param params.isEditMode      - Whether the builder is in edit mode
 * @param params.lockGlobalAction - RTK mutation trigger for POST /:id/lock
 *
 * @returns `clearLockRefreshTimer` — call before save/cancel to stop the timer.
 */
const useLockRefresh = ({
  id,
  isEditMode,
  lockGlobalAction,
  unlockGlobalAction,
}: UseLockRefreshParams): UseLockRefreshReturn => {
  const lockRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref so the cleanup closure always calls the latest trigger without re-running the effect
  const unlockRef = useRef(unlockGlobalAction);
  unlockRef.current = unlockGlobalAction;

  const clearLockRefreshTimer = useCallback(() => {
    if (lockRefreshTimerRef.current !== null) {
      clearTimeout(lockRefreshTimerRef.current);
      lockRefreshTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;

    const scheduleRefresh = (expiresAt: string | null) => {
      if (lockRefreshTimerRef.current !== null) {
        clearTimeout(lockRefreshTimerRef.current);
        lockRefreshTimerRef.current = null;
      }
      let delayMs: number;
      if (expiresAt) {
        // Refresh 10 seconds before expiry to ensure we renew well before it expires;
        // enforce a 5s minimum to avoid very rapid retry loops for near-expiry
        const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
        delayMs = Math.max(Math.floor(msUntilExpiry - 10000), 5000);
      } else {
        delayMs = 45 * 1000; // 45-second fallback
      }
      lockRefreshTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        try {
          const response = await lockGlobalAction(id).unwrap();
          if (!cancelled) {
            scheduleRefresh(extractLockExpiry(response));
          }
        } catch {
          // Refresh failed silently — lock will expire naturally
        }
      }, delayMs);
    };

    lockGlobalAction(id)
      .unwrap()
      .then((response) => {
        if (cancelled) return;
        scheduleRefresh(extractLockExpiry(response));
      })
      .catch(() => {
        if (!cancelled) scheduleRefresh(null);
      });

    return () => {
      cancelled = true;
      clearLockRefreshTimer();
      // Release the lock on unmount (SPA navigation away, component teardown, etc.)
      // Intentionally fire-and-forget — the component is already gone
      unlockRef.current(id);
    };
  }, [isEditMode, id, lockGlobalAction, clearLockRefreshTimer]);

  return { clearLockRefreshTimer };
};

export default useLockRefresh;
